import store from "@/src/store";

// Circular import를 피하기 위해 동적 import 사용
let sendbirdApi: any;

interface TokenInfo {
  token: string;
  expiresAt?: number;
  expiresIn?: number;
}

class SendbirdTokenManager {
  private static instance: SendbirdTokenManager;
  private tokenInfo: TokenInfo | null = null;
  private refreshTimer: NodeJS.Timeout | null = null;

  private constructor() {}

  static getInstance(): SendbirdTokenManager {
    if (!SendbirdTokenManager.instance) {
      SendbirdTokenManager.instance = new SendbirdTokenManager();
    }
    return SendbirdTokenManager.instance;
  }

  async getToken(): Promise<string | null> {
    // 토큰이 없거나 만료되었으면 새로 발급
    if (!this.tokenInfo || this.isTokenExpired()) {
      await this.refreshToken();
    }
    
    return this.tokenInfo?.token || null;
  }

  private isTokenExpired(): boolean {
    if (!this.tokenInfo) return true;
    
    if (this.tokenInfo.expiresAt) {
      // 5분의 여유를 두고 만료 체크
      return Date.now() > (this.tokenInfo.expiresAt - 5 * 60 * 1000);
    }
    
    return false;
  }

  private async refreshToken(): Promise<void> {
    try {
      
      // Lazy import to avoid circular dependency
      if (!sendbirdApi) {
        const module = await import("./index");
        sendbirdApi = module.sendbirdApi;
      }
      
      const result = await store.dispatch(
        sendbirdApi.endpoints.getSendbirdToken.initiate()
      );
      
      // 에러 체크
      if ('error' in result) {
        throw new Error("Failed to refresh token");
      }

      if (result.data) {
        this.tokenInfo = {
          token: result.data.sendbird_token,
          expiresAt: result.data.expires_at,
          expiresIn: result.data.expires_in,
        };

        // 자동 갱신 스케줄링
        this.scheduleRefresh();
        
      }
    } catch (error) {
      console.error("Failed to refresh Sendbird token:", error);
      this.tokenInfo = null;
    }
  }

  private scheduleRefresh(): void {
    // 기존 타이머 제거
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    if (!this.tokenInfo) return;

    let refreshIn: number;
    
    if (this.tokenInfo.expiresIn) {
      // expires_in이 있으면 그보다 5분 전에 갱신
      refreshIn = Math.max((this.tokenInfo.expiresIn - 300) * 1000, 0);
    } else if (this.tokenInfo.expiresAt) {
      // expires_at이 있으면 만료 5분 전에 갱신
      refreshIn = Math.max(this.tokenInfo.expiresAt - Date.now() - 300000, 0);
    } else {
      // 기본값: 50분 후 갱신
      refreshIn = 50 * 60 * 1000;
    }

    
    this.refreshTimer = setTimeout(() => {
      this.refreshToken();
    }, refreshIn);
  }

  clearToken(): void {
    this.tokenInfo = null;
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
}

export default SendbirdTokenManager.getInstance();